import { SelectionCard } from '@jouzen/ecom-components/v3/SelectionCard';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import { EventType } from '@/analytics/types';
import MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import type WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';
import useCurrency from '@/app/hooks/useCurrency';
import type { ListProduct } from '@/queries/Products';

import {
  PDP_NEW_INTERSTITIAL,
  PDP_NEW_PREPAID_INTERSTITIAL,
} from '../_consts/pdpVariants';
import type { Order } from '../_types/order';
import { getPriceAndDiscount } from '../_utils/getPriceAndDiscount';
import CheckboxGroup from './CheckboxGroup';
import OneYearMembershipContent from './OneYearMembershipContent';
import ProductSelectCheckbox from './ProductSelectCheckbox';

interface OneYearMembershipSelectionProps {
  readonly onMembershipSelection: (
    value: MembershipSelection | WarrantySelection,
  ) => void;
  readonly order: Order;
  readonly variant: number;
  readonly yearlyMembership: ListProduct | undefined;
}

const OneYearMembershipSelection = ({
  onMembershipSelection,
  order,
  variant,
  yearlyMembership,
}: OneYearMembershipSelectionProps) => {
  const t = useTranslations();
  const { currencyCode } = useCurrency();

  const { price } = getPriceAndDiscount(yearlyMembership);
  const selectedMembership = order.membership.value as MembershipSelection;

  const isDefaultSelector =
    variant !== PDP_NEW_INTERSTITIAL &&
    variant !== PDP_NEW_PREPAID_INTERSTITIAL;

  // analytics tracking for membership selection and checkbox change
  // this is used to track the selection of one-year membership
  const handleMembershipSelection = async (): Promise<void> => {
    if (selectedMembership === MembershipSelection.OneYear) {
      await window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: 'pay as you go',
        location: 'pdp',
        step: 3,
      });
      onMembershipSelection(MembershipSelection.OneMonth);
    } else {
      await window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: 'buy 12 months up front',
        location: 'pdp',
        step: 3,
      });
      onMembershipSelection(MembershipSelection.OneYear);
    }
  };

  const handleMembershipCheckboxChange = async (value: string[]) => {
    if (value.includes(MembershipSelection.OneYear)) {
      await window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: 'buy 12 months up front',
        location: 'pdp',
        step: 3,
      });
      onMembershipSelection(MembershipSelection.OneYear);
    } else {
      await window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: 'pay as you go',
        location: 'pdp',
        step: 3,
      });
      onMembershipSelection(MembershipSelection.OneMonth);
    }
  };

  return (
    <div
      className="row-start-2 border-b border-gray-300 pb-10 md:border-b-[0px] md:pb-0"
      data-cy="membership-section"
    >
      {isDefaultSelector ? (
        <SelectionCard
          cardSelected={selectedMembership === MembershipSelection.OneYear}
          currencyCode={currencyCode}
          data-cy="bundle-membership-one-year"
          onCheckBoxClick={handleMembershipSelection}
          price={price ?? 0}
          title={t('12_months_prepaid')}
        />
      ) : (
        <CheckboxGroup
          label={t('bundle_cart_title')}
          onChange={handleMembershipCheckboxChange}
          value={[selectedMembership]}
        >
          <ProductSelectCheckbox
            aria-label={t('12_months_prepaid')}
            value={MembershipSelection.OneYear}
          >
            {(state) => (
              <OneYearMembershipContent
                currencyCode={currencyCode}
                price={price!}
                {...state}
              />
            )}
          </ProductSelectCheckbox>
        </CheckboxGroup>
      )}
      <Typography
        className={cx('italic', { 'mt-3': !isDefaultSelector })}
        color="gray"
        variant="body-small"
      >
        {t('add_ons_one_year_prepaid_copy')}
      </Typography>
    </div>
  );
};

export default OneYearMembershipSelection;
